// ═══════════════════════════════════════════════════════════════
// SHELTER MANAGEMENT — Transfers
// Create and manage survivor transfers between shelters
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select,
  Textarea, Modal, Stack, SimpleGrid, Drawer, Divider,
} from '@mantine/core';
import {
  IconSearch, IconPlus, IconRefresh, IconArrowRight,
  IconTruckDelivery, IconCheck, IconX,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { useShelterStore } from '../../store/shelterStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import type { ShelterTransfer, TransferStatus } from '../../types/shelter';

const STATUS_COLORS: Record<TransferStatus, string> = {
  pending: 'yellow', in_transit: 'blue', completed: 'green', cancelled: 'gray',
};

const EMPTY: Partial<ShelterTransfer> = {
  survivorId: '', fromShelterId: '', fromShelterName: '', toShelterId: '',
  toShelterName: '', reason: '', notes: '',
};

export default function Transfers() {
  const navigate = useNavigate();
  const { activeIncident, user } = useAuthStore();
  const { canEdit } = useModulePermission('shelter');
  const incidentId = activeIncident?.id?.toString() ?? '';

  const {
    transfers, survivors, shelters,
    loadingTransfers,
    loadTransfers, loadSurvivors, loadShelters,
    createTransfer, updateTransfer, updateSurvivor,
  } = useShelterStore();

  const [search, setSearch] = useState('');

  const [sortField, setSortField] = useState<string>('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };
  const sortIcon = (f: string) => sortField === f ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [detailTarget, setDetailTarget] = useState<ShelterTransfer | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<Partial<ShelterTransfer>>({ ...EMPTY });
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadTransfers(); loadSurvivors(); loadShelters(); }, [loadTransfers, loadSurvivors, loadShelters]);

  // Store already server-side filters by active incident; use directly
  const iTransfers = incidentId ? transfers : [];
  const iSurvivors = incidentId ? survivors : [];
  const iShelters = incidentId ? shelters : [];

  // Lookup maps
  const survivorMap = useMemo(() => {
    const m = new Map<string, string>();
    iSurvivors.forEach(s => m.set(String(s.Rid), `${s.firstName} ${s.lastName}`));
    return m;
  }, [iSurvivors]);

  const shelterMap = useMemo(() => {
    const m = new Map<string, string>();
    iShelters.forEach(s => m.set(String(s.Rid), s.name));
    return m;
  }, [iShelters]);

  const survivorOptions = useMemo(() =>
    iSurvivors
      .filter(s => s.status === 'checked_in')
      .map(s => ({ value: String(s.Rid), label: `${s.firstName} ${s.lastName}` })),
    [iSurvivors],
  );

  const shelterOptions = useMemo(() =>
    iShelters.map(s => ({ value: String(s.Rid), label: s.name })),
    [iShelters],
  );

  const filtered = useMemo(() => {
    let list = iTransfers;
    if (statusFilter && statusFilter !== 'all') list = list.filter(t => t.status === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(t =>
        t.survivorName.toLowerCase().includes(q) ||
        t.fromShelterName.toLowerCase().includes(q) ||
        t.toShelterName.toLowerCase().includes(q) ||
        t.reason.toLowerCase().includes(q) ||
        (survivorMap.get(t.survivorId) ?? '').toLowerCase().includes(q),
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      if (!sortField) return new Date(b.initiatedAt).getTime() - new Date(a.initiatedAt).getTime();
      const va = (a as any)[sortField] ?? '';
      const vb = (b as any)[sortField] ?? '';
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [iTransfers, statusFilter, search, survivorMap, sortField, sortDir]);

  const setField = (key: keyof ShelterTransfer, value: string) => setForm(prev => ({ ...prev, [key]: value }));

  // Auto-fill fromShelter when survivor is selected
  const handleSurvivorSelect = (survivorId: string | null) => {
    if (!survivorId) { setField('survivorId', ''); return; }
    const survivor = iSurvivors.find(s => String(s.Rid) === survivorId);
    const fromName = survivor ? (shelterMap.get(survivor.shelterId) ?? '') : '';
    setForm(prev => ({
      ...prev,
      survivorId,
      fromShelterId: survivor?.shelterId ?? '',
      fromShelterName: fromName,
    }));
  };

  const openCreate = () => {
    setForm({ ...EMPTY });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.survivorId || !form.toShelterId) {
      notifications.show({ message: 'Survivor and destination shelter are required', color: 'yellow' });
      return;
    }
    if (form.fromShelterId === form.toShelterId) {
      notifications.show({ message: 'From and To shelter cannot be the same', color: 'yellow' });
      return;
    }
    setSaving(true);
    try {
      const survivor = iSurvivors.find(s => String(s.Rid) === form.survivorId);
      const toShelterName = shelterMap.get(form.toShelterId!) ?? '';

      const payload: Partial<ShelterTransfer> = {
        ...form,
        incidentId,
        survivorName: survivor ? `${survivor.firstName} ${survivor.lastName}` : '',
        toShelterName,
        status: 'pending',
        initiatedBy: user?.fullName ?? user?.email ?? '',
        initiatedAt: new Date().toISOString(),
      };

      await createTransfer(payload);
      notifications.show({ message: 'Transfer initiated', color: 'green' });
      setModalOpen(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'red' });
    } finally { setSaving(false); }
  };

  const handleStatusUpdate = async (transfer: ShelterTransfer, newStatus: TransferStatus) => {
    setSaving(true);
    try {
      const payload: Partial<ShelterTransfer> = { status: newStatus };
      if (newStatus === 'completed') {
        payload.completedAt = new Date().toISOString();
      }
      await updateTransfer(transfer.Rid, payload);

      // When completed, move the survivor to the new shelter
      if (newStatus === 'completed' && transfer.survivorId && transfer.toShelterId) {
        await updateSurvivor(Number(transfer.survivorId), { shelterId: transfer.toShelterId });
      }

      notifications.show({ message: `Transfer ${newStatus.replace(/_/g, ' ')}`, color: 'green' });
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Update failed', color: 'red' });
    } finally { setSaving(false); }
  };

  const getNextActions = (t: ShelterTransfer) => {
    if (!canEdit) return <Text size="xs" c="dimmed">-</Text>;
    switch (t.status) {
      case 'pending':
        return (
          <Group gap={4} justify="flex-end">
            <Button size="compact-xs" variant="light" color="blue"
              leftSection={<IconTruckDelivery size={12} />}
              onClick={() => handleStatusUpdate(t, 'in_transit')}>
              In Transit
            </Button>
            <Button size="compact-xs" variant="light" color="gray"
              leftSection={<IconX size={12} />}
              onClick={() => handleStatusUpdate(t, 'cancelled')}>
              Cancel
            </Button>
          </Group>
        );
      case 'in_transit':
        return (
          <Group gap={4} justify="flex-end">
            <Button size="compact-xs" variant="light" color="green"
              leftSection={<IconCheck size={12} />}
              onClick={() => handleStatusUpdate(t, 'completed')}>
              Complete
            </Button>
            <Button size="compact-xs" variant="light" color="gray"
              leftSection={<IconX size={12} />}
              onClick={() => handleStatusUpdate(t, 'cancelled')}>
              Cancel
            </Button>
          </Group>
        );
      default:
        return <Text size="xs" c="dimmed">-</Text>;
    }
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Transfers</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loadingTransfers} onClick={() => loadTransfers()}>Refresh</Button>
          {canEdit && (
            <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>New Transfer</Button>
          )}
        </Group>
      </Group>

      <Group mb="md" gap="sm" wrap="wrap">
        <TextInput placeholder="Search survivor, shelter, reason..."
          leftSection={<IconSearch size={14} />} value={search}
          onChange={e => setSearch(e.currentTarget.value)} size="xs"
          style={{ flex: 1, minWidth: 200 }} />
        <Select data={[
          { value: 'all', label: 'All Statuses' },
          { value: 'pending', label: 'Pending' },
          { value: 'in_transit', label: 'In Transit' },
          { value: 'completed', label: 'Completed' },
          { value: 'cancelled', label: 'Cancelled' },
        ]}
          value={statusFilter ?? 'all'} onChange={v => setStatusFilter(v)} size="xs" w={160} />
      </Group>

      <Text size="xs" c="dimmed" mb="xs">{filtered.length} transfer{filtered.length !== 1 ? 's' : ''}</Text>

      <Paper withBorder>
        <div style={{ overflowX: 'auto', maxHeight: 500, overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--mantine-color-gray-3)' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Survivor</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>From</th>
                <th style={{ padding: '8px 12px', textAlign: 'center' }}></th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>To</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Reason</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('status')}>Status{sortIcon('status')}</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Initiated By</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('createdAt')}>Date{sortIcon('createdAt')}</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={9} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                  {loadingTransfers ? 'Loading...' : 'No transfers found'}
                </td></tr>
              )}
              {filtered.map(t => (
                <tr key={t.id} onClick={() => setDetailTarget(t)}
                  style={{ cursor: 'pointer', borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
                  <td style={{ padding: '8px 12px', fontWeight: 500 }}>
                    {t.survivorName || survivorMap.get(t.survivorId) || t.survivorId || '-'}
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    {t.fromShelterName || shelterMap.get(t.fromShelterId) || '-'}
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                    <IconArrowRight size={14} color="#888" />
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    {t.toShelterName || shelterMap.get(t.toShelterId) || '-'}
                  </td>
                  <td style={{ padding: '8px 12px', maxWidth: 200 }}>
                    <Text size="sm" lineClamp={1}>{t.reason || '-'}</Text>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <Badge size="sm" color={STATUS_COLORS[t.status as TransferStatus] ?? 'gray'}>
                      {t.status ? t.status.replace(/_/g, ' ') : 'pending'}
                    </Badge>
                  </td>
                  <td style={{ padding: '8px 12px' }}>{t.initiatedBy || '-'}</td>
                  <td style={{ padding: '8px 12px' }}>
                    <Text size="sm" c="dimmed">
                      {t.initiatedAt ? new Date(t.initiatedAt).toLocaleDateString() : '-'}
                    </Text>
                    {t.completedAt && (
                      <Text size="xs" c="dimmed">
                        Completed: {new Date(t.completedAt).toLocaleDateString()}
                      </Text>
                    )}
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                    {getNextActions(t)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Paper>

      {/* Detail Drawer */}
      <Drawer opened={!!detailTarget} onClose={() => setDetailTarget(null)}
        position="right" size="md" title="Transfer Details">
        {detailTarget && (
          <Stack>
            <Group>
              <Badge size="lg" color={STATUS_COLORS[detailTarget.status as TransferStatus] ?? 'gray'}>
                {detailTarget.status ? detailTarget.status.replace(/_/g, ' ') : 'pending'}
              </Badge>
              <Text size="sm" c="dimmed">
                {detailTarget.initiatedAt ? new Date(detailTarget.initiatedAt).toLocaleString() : '-'}
              </Text>
            </Group>

            <Divider />

            <div>
              <Text size="xs" c="dimmed">Survivor</Text>
              <Group gap="xs">
                <Text size="sm" fw={500}>
                  {detailTarget.survivorName || survivorMap.get(detailTarget.survivorId) || detailTarget.survivorId || '-'}
                </Text>
                {detailTarget.survivorId && (
                  <Button size="compact-xs" variant="subtle"
                    onClick={() => { setDetailTarget(null); navigate('/shelter/survivor/' + detailTarget.survivorId); }}>
                    View Survivor
                  </Button>
                )}
              </Group>
            </div>

            <Divider label="Route" labelPosition="left" />

            <Group gap="sm" align="center">
              <div style={{ flex: 1 }}>
                <Text size="xs" c="dimmed">From Shelter</Text>
                <Text size="sm" fw={500}>{detailTarget.fromShelterName || shelterMap.get(detailTarget.fromShelterId) || '-'}</Text>
              </div>
              <IconArrowRight size={20} color="#888" />
              <div style={{ flex: 1 }}>
                <Text size="xs" c="dimmed">To Shelter</Text>
                <Text size="sm" fw={500}>{detailTarget.toShelterName || shelterMap.get(detailTarget.toShelterId) || '-'}</Text>
              </div>
            </Group>

            <Divider />

            <div>
              <Text size="xs" c="dimmed">Reason</Text>
              <Text size="sm">{detailTarget.reason || '-'}</Text>
            </div>

            <SimpleGrid cols={2}>
              <div>
                <Text size="xs" c="dimmed">Initiated By</Text>
                <Text size="sm">{detailTarget.initiatedBy || '-'}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">Initiated At</Text>
                <Text size="sm">{detailTarget.initiatedAt ? new Date(detailTarget.initiatedAt).toLocaleString() : '-'}</Text>
              </div>
            </SimpleGrid>

            {detailTarget.completedAt && (
              <div>
                <Text size="xs" c="dimmed">Completed At</Text>
                <Text size="sm">{new Date(detailTarget.completedAt).toLocaleString()}</Text>
              </div>
            )}

            <div>
              <Text size="xs" c="dimmed">Notes</Text>
              <Text size="sm">{detailTarget.notes || '-'}</Text>
            </div>

            <Divider />

            {canEdit && detailTarget.status === 'pending' && (
              <Group justify="flex-end" gap="xs">
                <Button size="xs" variant="light" color="blue" leftSection={<IconTruckDelivery size={14} />}
                  onClick={() => { handleStatusUpdate(detailTarget, 'in_transit'); setDetailTarget(null); }}>
                  Mark In Transit
                </Button>
                <Button size="xs" variant="light" color="gray" leftSection={<IconX size={14} />}
                  onClick={() => { handleStatusUpdate(detailTarget, 'cancelled'); setDetailTarget(null); }}>
                  Cancel
                </Button>
              </Group>
            )}
            {canEdit && detailTarget.status === 'in_transit' && (
              <Group justify="flex-end" gap="xs">
                <Button size="xs" variant="light" color="green" leftSection={<IconCheck size={14} />}
                  onClick={() => { handleStatusUpdate(detailTarget, 'completed'); setDetailTarget(null); }}>
                  Mark Complete
                </Button>
                <Button size="xs" variant="light" color="gray" leftSection={<IconX size={14} />}
                  onClick={() => { handleStatusUpdate(detailTarget, 'cancelled'); setDetailTarget(null); }}>
                  Cancel
                </Button>
              </Group>
            )}
          </Stack>
        )}
      </Drawer>

      {/* Create Transfer Modal */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)}
        title="New Transfer" centered size="lg">
        <Stack>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <Select label="Survivor" required searchable
              data={survivorOptions} value={form.survivorId ?? ''}
              onChange={handleSurvivorSelect}
              placeholder="Select survivor" />
            <TextInput label="From Shelter" readOnly
              value={form.fromShelterName || (form.fromShelterId ? shelterMap.get(form.fromShelterId) ?? '' : '')}
              styles={{ input: { backgroundColor: 'var(--mantine-color-gray-1)' } }} />
          </SimpleGrid>

          <Select label="To Shelter" required searchable
            data={shelterOptions} value={form.toShelterId ?? ''}
            onChange={v => {
              const name = v ? (shelterMap.get(v) ?? '') : '';
              setForm(prev => ({ ...prev, toShelterId: v ?? '', toShelterName: name }));
            }}
            placeholder="Select destination shelter" />

          <TextInput label="Reason" value={form.reason ?? ''}
            onChange={e => setField('reason', e.currentTarget.value)}
            placeholder="e.g. Medical needs, capacity, family reunification" />

          <Textarea label="Notes" value={form.notes ?? ''}
            onChange={e => setField('notes', e.currentTarget.value)} autosize minRows={2} />

          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button loading={saving} onClick={handleSave}>Initiate Transfer</Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
